import { useState, useEffect, useCallback } from 'react';
import {
  Database,
  Loader2,
  RefreshCw,
  AlertCircle,
  Search,
  ChevronRight,
  X,
  Edit,
  Trash2,
  Save,
  Copy,
  Check,
} from 'lucide-react';

// Types
interface DynamoDbTable {
  tableName: string;
  displayName: string;
  description: string;
  partitionKeyName: string;
  sortKeyName?: string;
  gsiNames: string[];
  itemCount: number;
}

interface QueryResponse {
  items: Record<string, unknown>[];
  count: number;
  lastEvaluatedKey?: string;
  hasMore: boolean;
}

// API helper
async function fetchApi<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || `HTTP ${response.status}`);
  }

  return response.json();
}

export function DynamoDbEditorPage() {
  // State
  const [tables, setTables] = useState<DynamoDbTable[]>([]);
  const [selectedTable, setSelectedTable] = useState<DynamoDbTable | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Query state
  const [partitionKey, setPartitionKey] = useState('');
  const [sortKeyPrefix, setSortKeyPrefix] = useState('');
  const [isQuerying, setIsQuerying] = useState(false);
  const [queryResult, setQueryResult] = useState<QueryResponse | null>(null);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string | undefined>();

  // Item viewer/editor
  const [selectedItem, setSelectedItem] = useState<Record<string, unknown> | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedItem, setEditedItem] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  // Load tables
  const loadTables = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchApi<DynamoDbTable[]>('/api/v1/admin/dynamodb/tables');
      setTables(data);
      if (data.length > 0 && !selectedTable) {
        setSelectedTable(data[0]);
      }
    } catch (err) {
      console.error('Failed to load tables:', err);
      // Use mock data for development
      const mockTables: DynamoDbTable[] = [
        {
          tableName: 'chat-messages',
          displayName: 'Chat Messages',
          description: 'Squad chat messages stored in DynamoDB',
          partitionKeyName: 'pk',
          sortKeyName: 'sk',
          gsiNames: ['GSI1'],
          itemCount: 12500,
        },
        {
          tableName: 'chat-user-squad-state',
          displayName: 'Chat User State',
          description: 'User read receipts and squad chat state',
          partitionKeyName: 'pk',
          sortKeyName: 'sk',
          gsiNames: [],
          itemCount: 8750,
        },
        {
          tableName: 'user-presence',
          displayName: 'User Presence',
          description: 'Real-time user presence and online status',
          partitionKeyName: 'pk',
          sortKeyName: 'sk',
          gsiNames: [],
          itemCount: 3200,
        },
      ];
      setTables(mockTables);
      if (!selectedTable) {
        setSelectedTable(mockTables[0]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [selectedTable]);

  // Query table
  const queryTable = async (loadMore = false) => {
    if (!selectedTable || !partitionKey) return;

    setIsQuerying(true);
    setError(null);

    try {
      const data = await fetchApi<QueryResponse>(`/api/v1/admin/dynamodb/${selectedTable.tableName}/query`, {
        method: 'POST',
        body: JSON.stringify({
          partitionKey,
          sortKeyPrefix: sortKeyPrefix || undefined,
          limit: 25,
          lastEvaluatedKey: loadMore ? lastEvaluatedKey : undefined,
        }),
      });

      if (loadMore && queryResult) {
        setQueryResult({
          ...data,
          items: [...queryResult.items, ...data.items],
          count: queryResult.count + data.count,
        });
      } else {
        setQueryResult(data);
      }
      setLastEvaluatedKey(data.lastEvaluatedKey);
    } catch (err) {
      console.error('Failed to query table:', err);
      // Mock data for development
      setQueryResult({
        items: getMockItems(),
        count: 5,
        hasMore: false,
      });
    } finally {
      setIsQuerying(false);
    }
  };

  // Save item
  const saveItem = async () => {
    if (!selectedTable || !selectedItem) return;

    setIsSaving(true);
    setError(null);

    try {
      const updates = JSON.parse(editedItem);
      const pk = selectedItem.pk as string;
      const sk = selectedItem.sk as string;

      await fetchApi(`/api/v1/admin/dynamodb/${selectedTable.tableName}/item?pk=${encodeURIComponent(pk)}&sk=${encodeURIComponent(sk)}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });

      setIsEditing(false);
      setSelectedItem(updates);
      // Refresh the query
      if (partitionKey) {
        queryTable();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save item');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete item
  const deleteItem = async () => {
    if (!selectedTable || !selectedItem) return;
    if (!confirm('Are you sure you want to delete this item? This cannot be undone.')) return;

    setIsSaving(true);
    setError(null);

    try {
      const pk = selectedItem.pk as string;
      const sk = selectedItem.sk as string;

      await fetchApi(`/api/v1/admin/dynamodb/${selectedTable.tableName}/item?pk=${encodeURIComponent(pk)}&sk=${encodeURIComponent(sk)}`, {
        method: 'DELETE',
      });

      setSelectedItem(null);
      // Refresh the query
      if (partitionKey) {
        queryTable();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete item');
    } finally {
      setIsSaving(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = async () => {
    if (!selectedItem) return;
    await navigator.clipboard.writeText(JSON.stringify(selectedItem, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Load on mount
  useEffect(() => {
    loadTables();
  }, [loadTables]);

  // Reset query when table changes
  useEffect(() => {
    setQueryResult(null);
    setSelectedItem(null);
    setPartitionKey('');
    setSortKeyPrefix('');
  }, [selectedTable]);

  // Mock items for development
  function getMockItems(): Record<string, unknown>[] {
    return [
      {
        pk: 'SQUAD#123',
        sk: 'MSG#1706900000000',
        squadId: 123,
        senderId: 456,
        senderName: 'John Smith',
        messageType: 'TEXT',
        messageText: 'Hey everyone, ready for tennis today?',
        createdAtEpochMs: 1706900000000,
        aiFlagged: false,
      },
      {
        pk: 'SQUAD#123',
        sk: 'MSG#1706900060000',
        squadId: 123,
        senderId: 789,
        senderName: 'Jane Doe',
        messageType: 'TEXT',
        messageText: "I'll be there at 3pm!",
        createdAtEpochMs: 1706900060000,
        aiFlagged: false,
      },
      {
        pk: 'SQUAD#123',
        sk: 'MSG#1706900120000',
        squadId: 123,
        senderId: 456,
        senderName: 'John Smith',
        messageType: 'TEXT',
        messageText: 'Perfect, see you then!',
        createdAtEpochMs: 1706900120000,
        aiFlagged: false,
      },
    ];
  }

  return (
    <div className="flex flex-col min-h-0 h-full">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Database className="h-7 w-7 text-cyan-400" />
            DynamoDB Editor
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            Query and edit DynamoDB tables
          </p>
        </div>

        <button
          onClick={loadTables}
          disabled={isLoading}
          className="inline-flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-300 rounded-lg transition-colors"
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          Refresh
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-3 p-4 mb-4 bg-red-900/20 border border-red-800/50 rounded-lg text-red-400">
          <AlertCircle className="h-5 w-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex gap-4 min-h-0">
        {/* Left panel - Tables */}
        <div className="w-64 flex-shrink-0 flex flex-col bg-slate-900/50 border border-slate-800 rounded-lg">
          <div className="px-3 py-2 border-b border-slate-800">
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Tables</span>
          </div>
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
              </div>
            ) : (
              <div className="py-1">
                {tables.map((table) => (
                  <button
                    key={table.tableName}
                    onClick={() => setSelectedTable(table)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${
                      selectedTable?.tableName === table.tableName
                        ? 'bg-cyan-500/10 text-cyan-400'
                        : 'hover:bg-slate-800/50 text-slate-300'
                    }`}
                  >
                    <Database className="h-4 w-4 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{table.displayName}</div>
                      <div className="text-xs text-slate-500">{table.itemCount.toLocaleString()} items</div>
                    </div>
                    <ChevronRight className={`h-4 w-4 transition-transform ${
                      selectedTable?.tableName === table.tableName ? 'rotate-90' : ''
                    }`} />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Center panel - Query and Results */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Query bar */}
          {selectedTable && (
            <div className="mb-4 p-4 bg-slate-800/50 border border-slate-700 rounded-lg">
              <div className="flex items-end gap-4">
                <div className="flex-1">
                  <label className="block text-xs text-slate-500 mb-1">
                    Partition Key ({selectedTable.partitionKeyName})
                  </label>
                  <input
                    type="text"
                    value={partitionKey}
                    onChange={(e) => setPartitionKey(e.target.value)}
                    placeholder="e.g., SQUAD#123"
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                  />
                </div>
                {selectedTable.sortKeyName && (
                  <div className="flex-1">
                    <label className="block text-xs text-slate-500 mb-1">
                      Sort Key Prefix ({selectedTable.sortKeyName})
                    </label>
                    <input
                      type="text"
                      value={sortKeyPrefix}
                      onChange={(e) => setSortKeyPrefix(e.target.value)}
                      placeholder="e.g., MSG#"
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                    />
                  </div>
                )}
                <button
                  onClick={() => queryTable()}
                  disabled={isQuerying || !partitionKey}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
                >
                  {isQuerying ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                  Query
                </button>
              </div>
              {selectedTable.description && (
                <p className="mt-2 text-xs text-slate-500">{selectedTable.description}</p>
              )}
            </div>
          )}

          {/* Results */}
          <div className="flex-1 overflow-auto rounded-lg border border-slate-800">
            {!queryResult ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-500">
                <Database className="h-12 w-12 mb-3 opacity-50" />
                <p>Enter a partition key and click Query</p>
              </div>
            ) : queryResult.items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-500">
                <Database className="h-12 w-12 mb-3 opacity-50" />
                <p>No items found</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-800">
                {queryResult.items.map((item, index) => (
                  <div
                    key={index}
                    onClick={() => {
                      setSelectedItem(item);
                      setEditedItem(JSON.stringify(item, null, 2));
                      setIsEditing(false);
                    }}
                    className={`p-4 cursor-pointer transition-colors ${
                      selectedItem === item
                        ? 'bg-cyan-500/10'
                        : 'hover:bg-slate-800/50'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-sm text-cyan-400">
                          {item.pk as string}
                        </div>
                        {item.sk !== undefined && item.sk !== null && (
                          <div className="font-mono text-xs text-slate-500">
                            {String(item.sk)}
                          </div>
                        )}
                      </div>
                      <div className="text-sm text-slate-400 truncate max-w-md">
                        {item.messageText as string || item.senderName as string || JSON.stringify(item).substring(0, 50) + '...'}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Load more */}
                {queryResult.hasMore && (
                  <div className="p-4 text-center">
                    <button
                      onClick={() => queryTable(true)}
                      disabled={isQuerying}
                      className="px-4 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-300 rounded-lg transition-colors"
                    >
                      {isQuerying ? 'Loading...' : 'Load More'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Results footer */}
          {queryResult && (
            <div className="mt-2 text-xs text-slate-500">
              {queryResult.count} item{queryResult.count !== 1 ? 's' : ''} loaded
              {queryResult.hasMore && ' (more available)'}
            </div>
          )}
        </div>

        {/* Right panel - Item viewer/editor */}
        {selectedItem && (
          <div className="w-[450px] flex-shrink-0 flex flex-col bg-slate-900 border border-slate-800 rounded-lg">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
              <span className="font-medium text-white">Item Details</span>
              <div className="flex items-center gap-1">
                <button
                  onClick={copyToClipboard}
                  className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-white"
                  title="Copy JSON"
                >
                  {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                </button>
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className={`p-1.5 rounded hover:bg-slate-800 ${isEditing ? 'text-cyan-400' : 'text-slate-400 hover:text-white'}`}
                  title="Edit"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={deleteItem}
                  disabled={isSaving}
                  className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-red-400"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setSelectedItem(null)}
                  className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-white"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {isEditing ? (
                <textarea
                  value={editedItem}
                  onChange={(e) => setEditedItem(e.target.value)}
                  className="w-full h-full font-mono text-sm bg-slate-800 border border-slate-700 rounded p-3 text-slate-300 focus:outline-none focus:ring-1 focus:ring-cyan-500 resize-none"
                  spellCheck={false}
                />
              ) : (
                <pre className="font-mono text-sm text-slate-300 whitespace-pre-wrap">
                  {JSON.stringify(selectedItem, null, 2)}
                </pre>
              )}
            </div>

            {/* Footer */}
            {isEditing && (
              <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-800">
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setEditedItem(JSON.stringify(selectedItem, null, 2));
                  }}
                  className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={saveItem}
                  disabled={isSaving}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white rounded-lg transition-colors"
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  Save
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
